import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Pencil, Plus } from "lucide-react"
import { listBlueprints, getTokenBalance, getUserTier } from "@/services/api"
import { LoadingSpinner } from "@/components/ui/loading-spinner"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { CARD_CATEGORIES } from "@/lib/utils/constants/cards"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { toast } from '@/hooks/useToast';

interface Blueprint {
  id: string;
  name: string;
  description: string;
  configuration: any;
  userId: string;
  username: string;
  version: string;
  theme_id: number;
  theme: string;
  createdAt: string;
  updatedAt: string;
}

export function BlueprintList() {
  const [blueprints, setBlueprints] = useState<Blueprint[]>([])
  const [loading, setLoading] = useState(true)
  const [showAll, setShowAll] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [userTier, setUserTier] = useState<string>('starter')
  const navigate = useNavigate()

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const response = await getTokenBalance();
        setIsAdmin(response.data.data.tier === 'admin');
      } catch (error) {
        console.error('Failed to check admin status:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, []);

  useEffect(() => {
    const checkTier = async () => {
      const tier = await getUserTier();
      setUserTier(tier);
    };
    checkTier();
  }, []);

  useEffect(() => {
    const fetchBlueprints = async () => {
      try {
        const response = await listBlueprints(showAll && isAdmin)
        console.log('Raw response:', response)
        const blueprintsData = Array.isArray(response?.data) 
          ? response.data.map((bp: any) => ({
              ...bp,
              theme_id: bp.theme_id,
              theme: getThemeName(bp.theme_id)
            }))
          : [];
        console.log('Processed blueprints:', blueprintsData);
        setBlueprints(blueprintsData)
      } catch (error) {
        console.error('Failed to fetch blueprints:', error)
        setBlueprints([])
      } finally {
        setLoading(false)
      }
    }

    fetchBlueprints()
  }, [showAll, isAdmin])

  const getThemeName = (themeId: number): string => {
    const category = Object.values(CARD_CATEGORIES).find(
      cat => cat.themeId === themeId
    );
    return category?.label || 'Unknown';
  };

  const hasAccess = (tier: string): boolean => {
    return tier === 'admin' || tier === 'starter';
  };

  const handleCreateNew = () => {
    if (!hasAccess(userTier)) {
      toast({
        variant: "destructive",
        title: "Feature not available",
        description: "Please upgrade to Starter tier to create blueprints",
      });
      return;
    }
    navigate('/blueprints/create');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen md:ml-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="container mx-auto p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold">Blueprints</h1>
        <div className="flex items-center gap-4">
          {isAdmin && (
            <div className="flex items-center space-x-2">
              <Switch
                id="show-all"
                checked={showAll}
                onCheckedChange={setShowAll}
              />
              <Label htmlFor="show-all">Show All Blueprints</Label>
            </div>
          )}
          <Button onClick={handleCreateNew}>
            <Plus className="mr-2 h-4 w-4" /> Create New Blueprint
          </Button>
        </div>
      </div>
      
      {/* Only show alert after we confirm user is free tier */}
      {userTier === 'free' && (
        <Alert className="mb-4 bg-muted">
          <AlertDescription className="flex items-center justify-between">
            <span>Upgrade to Starter tier to create blueprints</span>
            <Button 
              variant="default"
              onClick={() => navigate('/pricing')}
              className="ml-4"
            >
              Upgrade Now
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Content */}
      <div className="rounded-md border bg-card">
        {blueprints.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <p className="text-muted-foreground mb-4">No blueprints found</p>
            <Button onClick={handleCreateNew}>
              <Plus className="mr-2 h-4 w-4" /> Create Your First Blueprint
            </Button>
          </div>
        ) : (
          <ScrollArea className="h-[calc(100vh-12rem)]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Name</TableHead>
                  <TableHead className="hidden md:table-cell">Description</TableHead>
                  <TableHead className="hidden sm:table-cell">Created By</TableHead>
                  <TableHead className="w-[80px] hidden lg:table-cell">Version</TableHead>
                  <TableHead className="w-[100px] hidden lg:table-cell">Theme</TableHead>
                  <TableHead className="hidden lg:table-cell">Created At</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blueprints.map((blueprint) => (
                  <TableRow key={blueprint.id}>
                    <TableCell className="font-medium">{blueprint.name}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {blueprint.description}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {blueprint.username}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-xs bg-blue-500/10 text-blue-500 dark:bg-blue-500/20 dark:text-blue-400 px-2 py-1 rounded">
                        v{blueprint.version}
                      </span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-xs bg-purple-500/10 text-purple-500 dark:bg-purple-500/20 dark:text-purple-400 px-2 py-1 rounded">
                        {blueprint.theme}
                      </span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {new Date(blueprint.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/blueprints/edit/${blueprint.id}`)}
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">Edit</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </div>
    </div>
  )
} 